import { toBoolean } from '@apia/util';
import React from 'react';
import { InputProps, Switch, SwitchProps } from '@apia/theme';
import { useField } from '../hooks/useField';
import { useValidationClass } from '../hooks/useValidationClass';
import { FieldWrapper } from './FieldWrapper';
import { TDefaultFieldType } from './types';

export type TSwitchbox = Omit<SwitchProps, 'onChange'> &
  Pick<InputProps, 'onChange'> &
  TDefaultFieldType & { reverse?: boolean };

export const Switchbox = React.forwardRef<HTMLInputElement, TSwitchbox>(
  (
    {
      avoidSemicolon,
      className,
      label,
      onChange: outerOnChange,
      type,
      reverse = false,
      avoidRegisterIfExists,
      formName,
      initialValue,
      name,
      removeOnUnmount,
      submitValueParser,
      validationClass,
      validationFunction,
      validationRules,
      value,
      ...props
    },
    ref,
  ) => {
    const { actualValidationFunction, actualValidationRules } =
      useValidationClass({
        formName,
        name,
        validationClass,
        validationFunction,
        validationRules,
      });
    const { onChange, updateValidations, isTouched, ...inputProps } =
      useField<string>({
        avoidRegisterIfExists,
        formName,
        name,
        initialValue:
          (initialValue ?? 'off') === 'on' || toBoolean(initialValue)
            ? 'on'
            : 'off',
        removeOnUnmount,
        submitValueParser,
        validationFunction: actualValidationFunction,
        validationRules: actualValidationRules,
      });

    return (
      <FieldWrapper
        name={name}
        label={label}
        className={`switchbox ${className ?? ''}`}
        avoidSemicolon={avoidSemicolon}
      >
        <Switch
          {...props}
          {...inputProps}
          aria-label={props['aria-label'] ?? label}
          onChange={(ev) => {
            onChange(ev.target.checked !== reverse ? 'on' : 'off');
            if (outerOnChange) {
              outerOnChange(ev);
            }
          }}
          checked={((inputProps.value ?? 'off') === 'on') !== reverse}
          ref={ref}
        />
      </FieldWrapper>
    );
  },
);

Switchbox.displayName = 'Switchbox';
